'use client';

import type { MouseEvent } from 'react';

const MIN_GENERATION_QUERY_LENGTH = 3;

export function normalizePlantProfileGenerationQuery(query: string): string {
  return query.trim().replace(/\s+/g, ' ');
}

export function canOfferPlantProfileGeneration(query: string, nameMatchCount: number): boolean {
  const normalizedQuery = normalizePlantProfileGenerationQuery(query);
  return (
    normalizedQuery.length >= MIN_GENERATION_QUERY_LENGTH &&
    /[A-Za-z]/.test(normalizedQuery) &&
    nameMatchCount === 0
  );
}

export function countPlantProfileNameMatches(
  query: string,
  plants: Array<{ name: string; scientificName?: string | null }>,
): number {
  const normalizedQuery = normalizePlantProfileGenerationQuery(query).toLowerCase();
  if (!normalizedQuery) return 0;

  return plants.filter(plant =>
    plant.name.toLowerCase().includes(normalizedQuery) ||
    (plant.scientificName ?? '').toLowerCase().includes(normalizedQuery)
  ).length;
}

export default function MissingPlantProfileAction({
  query,
  isGenerating,
  onCreate,
  activation = 'click',
  className = '',
}: {
  query: string;
  isGenerating: boolean;
  onCreate: () => void;
  activation?: 'click' | 'mouseDown';
  className?: string;
}) {
  const trimmedQuery = normalizePlantProfileGenerationQuery(query);

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (activation !== 'click') return;
    event.preventDefault();
    onCreate();
  }

  function handleMouseDown(event: MouseEvent<HTMLButtonElement>) {
    if (activation !== 'mouseDown') return;
    event.preventDefault();
    onCreate();
  }

  if (isGenerating) {
    return (
      <p className={`font-body text-[13px] text-sage italic ${className}`}>
        Creating community plant profile for {trimmedQuery}...
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      className={className}
    >
      Create community plant profile for &lsquo;{trimmedQuery}&rsquo;
    </button>
  );
}
